/**
 * Два входа в одно приложение: сотрудники — на основном адресе
 * (`hub.braining.group`), подрядчики — на своём поддомене
 * (`partners.braining.group`).
 *
 * Отдельный поддомен — не косметика. Сессионная кука привязана к хосту, и
 * браузер подрядчика никогда не понесёт её на адрес сотрудников. Даже если
 * где-то в проверке прав окажется дыра, у подрядчика в браузере просто нет
 * входа туда, где сметы. Настоящая граница — роль: серверные функции и RLS
 * проверяют её на каждом запросе, а заголовок Host можно подделать руками.
 * Портал — второй замок поверх неё, и он закрывает то, что роль не видит:
 * чужую страницу в браузере подрядчика, переход по ссылке, подсказку в форме
 * входа.
 *
 * Модуль без серверных и браузерных зависимостей: им пользуются и
 * middleware, и корневой маршрут, и тесты.
 */

export type Portal = "staff" | "partners";

/**
 * `docs` — менеджер по документам (ответ Даши 21.10: документы ведут как
 * минимум трое — две команды HR Comm и маркетинг). Сотрудник, но работает
 * с подрядчиками и сделками, а не со сметами.
 */
export type StaffRole = "admin" | "manager" | "rop" | "docs";
export type UserRole = StaffRole | "contractor";

export const STAFF_ROLES: readonly StaffRole[] = ["admin", "manager", "rop", "docs"];

/** Префикс поддомена подрядчиков. Локально — `partners.localhost:3000`. */
export const PARTNERS_PREFIX = "partners.";

export function isStaffRole(role: string | null | undefined): role is StaffRole {
  return role === "admin" || role === "manager" || role === "rop" || role === "docs";
}

/** Кто ведёт документооборот: подрядчики, юрлица, сделки. */
export function canDocflow(role: string | null | undefined): boolean {
  return role === "admin" || role === "rop" || role === "docs";
}

/** Портал по заголовку Host или `location.hostname`. Порт отбрасывается. */
export function portalOfHost(host: string | null | undefined): Portal {
  const name = (host ?? "").trim().toLowerCase().replace(/:\d+$/, "");
  return name.startsWith(PARTNERS_PREFIX) ? "partners" : "staff";
}

/** Какой портал положен роли. */
export function portalOfRole(role: UserRole): Portal {
  return role === "contractor" ? "partners" : "staff";
}

/**
 * Кого пускает серверная функция.
 *  - `staff` — любой сотрудник (бывший «любой вошедший»);
 *  - `lead` — администратор или руководитель: справочники, жалобы;
 *  - `docflow` — кто ведёт документы: администратор, руководитель и
 *    менеджер по документам;
 *  - `admin` — только администратор;
 *  - `partner` — только подрядчик.
 */
export type Need = "staff" | "lead" | "docflow" | "admin" | "partner";

export type Admission = { ok: true } | { ok: false; status: 401 | 403 };

/**
 * Решение о допуске — одной чистой функцией, чтобы его можно было
 * проверить перебором всех сочетаний, а не верить на слово четырём
 * middleware.
 */
export function admit(role: UserRole | null | undefined, portal: Portal, need: Need): Admission {
  if (!role) return { ok: false, status: 401 };
  // Сессия не своего портала: подрядчик с кукой на адресе сотрудников или
  // наоборот. В браузере так не бывает — только руками подставленный Host.
  if (portalOfRole(role) !== portal) return { ok: false, status: 403 };
  const allowed =
    need === "partner"
      ? role === "contractor"
      : need === "staff"
        ? isStaffRole(role)
        : need === "lead"
          ? role === "admin" || role === "rop"
          : need === "docflow"
            ? canDocflow(role)
            : role === "admin";
  return allowed ? { ok: true } : { ok: false, status: 403 };
}

/**
 * Адрес кабинета подрядчика для ссылок, которые выдаёт сотрудник.
 *
 * `PARTNERS_URL` задаётся в окружении явно: угадывать по `PUBLIC_URL`
 * ненадёжно (у стенда `braining.sborka.io` подмена первого уровня дала бы
 * чужой `partners.sborka.io`). Исключение — локальная разработка:
 * `localhost:3000` → `partners.localhost:3000`, браузеры сами ведут любой
 * `*.localhost` на эту машину. Без адреса — `null`, и экран так и скажет.
 */
export function partnersBaseUrl(env: {
  PARTNERS_URL?: string;
  PUBLIC_URL?: string;
}): string | null {
  const explicit = env.PARTNERS_URL?.trim().replace(/\/+$/, "");
  if (explicit) return explicit;
  try {
    const url = new URL(env.PUBLIC_URL?.trim() ?? "");
    if (url.hostname !== "localhost") return null;
    url.hostname = PARTNERS_PREFIX + url.hostname;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * Чужой ли это вызов серверной функции — защита от подделки запроса (CSRF).
 *
 * TanStack принимает вызов и HTML-формой, и `text/plain`, и не смотрит, с
 * какой страницы он пришёл. С чужих сайтов спасает кука `SameSite=Lax`, но
 * `partners.braining.group` и `hub.braining.group` — один сайт, и кука
 * сотрудника уходит и на запросы со страниц партнёров. Поэтому для HTTP-вызова
 * функции требуем заголовок, который ставит наш клиент (`x-tsr-serverFn`): форма
 * его поставить не может, а чужой `fetch` с ним упирается в CORS. И если
 * браузер прислал `Origin`, он должен совпасть с хостом запроса.
 *
 * Вызов при серверной отрисовке идёт внутри процесса, адрес — страницы, а не
 * функции; его не трогаем. Возвращает причину отказа или `null`.
 */
export function foreignCall(req: {
  path: string;
  /** префикс адресов серверных функций (`TSS_SERVER_FN_BASE`) */
  fnBase: string;
  tsrHeader: string | null | undefined;
  origin: string | null | undefined;
  host: string | null | undefined;
}): string | null {
  const base = req.fnBase.endsWith("/") ? req.fnBase : `${req.fnBase}/`;
  if (!req.path.startsWith(base)) return null;
  if (req.tsrHeader !== "true") return "вызов не из приложения";
  if (req.origin) {
    let originHost: string;
    try {
      originHost = new URL(req.origin).host.toLowerCase();
    } catch {
      return "непонятный Origin";
    }
    if (originHost !== (req.host ?? "").toLowerCase()) return "вызов с чужого адреса";
  }
  return null;
}
